import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SlidersHorizontal, Loader2, Building2, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";

export const MAX_STANDAARD = 6;

export const DASHBOARD_SECTIES = [
  { key: "kpi", label: "KPI-tegels (tickets, verlof, taken, assets)" },
  { key: "aandacht", label: "Aandacht nodig (storingen, open punten)" },
  { key: "signaleringen", label: "Signaleringen" },
  { key: "sync", label: "Sync-status koppelingen" },
  { key: "contracten", label: "Contract-alerts" },
  { key: "mijntaken", label: "Wat moet ik nu doen" },
  { key: "dagoverzicht", label: "Dagoverzicht (vandaag / morgen / week)" },
  { key: "vergaderingen", label: "Aankomende vergaderingen" },
  { key: "tickets", label: "Openstaande tickets" },
  { key: "verlof", label: "Recente verlofaanvragen" },
  { key: "taken", label: "Mijn taken vandaag" },
  { key: "assets", label: "Assets · keuringsstatus" },
  { key: "aiusage", label: "AI-gebruik (widget)" },
  { key: "verjaardagen", label: "Verjaardagen deze maand" },
  { key: "herinneringen", label: "Vergader-herinneringen" },
  { key: "snelkoppelingen", label: "Snelkoppelingen" },
  { key: "aitrend", label: "AI-gebruik trend (grafiek)" },
];
const LABEL = Object.fromEntries(DASHBOARD_SECTIES.map((s) => [s.key, s.label]));

/**
 * Standaard zichtbaar = directie-standaard voor de rol (of de eerste 6 beschikbare blokken) + eigen 'getoond' − eigen 'verborgen'.
 * Alles wat beschikbaar is maar niet standaard zichtbaar staat achter 'Meer tonen'.
 */
export function useDashboardIndeling(beschikbaar = []) {
  const [indeling, setIndeling] = useState({ verborgen: [], getoond: [], compact: false, standaard: null });
  const [meerOpen, setMeerOpen] = useState(false);
  useEffect(() => {
    api.get("/me/dashboard-indeling")
      .then(({ data }) => setIndeling({ verborgen: data.verborgen || [], getoond: data.getoond || [], compact: !!data.compact, standaard: data.standaard ?? null }))
      .catch(() => {});
  }, []);
  const standaardSet = useMemo(() => {
    const basis = indeling.standaard?.length ? indeling.standaard.filter((k) => beschikbaar.includes(k)) : beschikbaar.slice(0, MAX_STANDAARD);
    return new Set([...basis, ...indeling.getoond.filter((k) => beschikbaar.includes(k))].filter((k) => !indeling.verborgen.includes(k)));
  }, [indeling, beschikbaar]);
  const zichtbaar = (key) => beschikbaar.includes(key) && !indeling.verborgen.includes(key) && (meerOpen || standaardSet.has(key));
  const achterMeer = beschikbaar.filter((k) => !indeling.verborgen.includes(k) && !standaardSet.has(k));
  return { indeling, setIndeling, zichtbaar, meerOpen, setMeerOpen, achterMeer, standaardSet, directieStandaard: !!indeling.standaard?.length };
}

export const MeerTonenKnop = ({ achterMeer, meerOpen, setMeerOpen }) => {
  if (!achterMeer.length) return null;
  return (
    <button type="button" onClick={() => setMeerOpen(!meerOpen)} data-testid="dashboard-meer-tonen"
            className="w-full rounded-xl border border-dashed border-border bg-white/70 hover:bg-white px-4 py-3 text-sm text-slate-600 hover:text-slate-900 inline-flex items-center justify-center gap-2 transition-colors">
      {meerOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      {meerOpen ? "Minder tonen" : `Meer tonen (${achterMeer.length} ${achterMeer.length === 1 ? "blok" : "blokken"})`}
      {!meerOpen && <span className="hidden sm:inline text-xs text-slate-400">· {achterMeer.slice(0, 3).map((k) => LABEL[k]?.split(" (")[0]).join(", ")}{achterMeer.length > 3 ? "…" : ""}</span>}
    </button>
  );
};

export const DashboardIndelingKnop = ({ indeling, onChange, beschikbaar = [], standaardSet }) => {
  const [open, setOpen] = useState(false);
  const [lokaal, setLokaal] = useState(indeling);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLokaal(indeling); }, [indeling, open]);
  const rolStandaard = (key) => (indeling.standaard?.length ? indeling.standaard : beschikbaar.slice(0, MAX_STANDAARD)).includes(key);
  const aan = (key) => !lokaal.verborgen.includes(key) && (rolStandaard(key) || lokaal.getoond.includes(key));
  const toggle = (key) => setLokaal((l) => {
    const nuAan = aan(key);
    return nuAan
      ? { ...l, verborgen: [...l.verborgen, key], getoond: l.getoond.filter((k) => k !== key) }
      : { ...l, verborgen: l.verborgen.filter((k) => k !== key), getoond: rolStandaard(key) ? l.getoond : [...l.getoond, key] };
  });
  const opslaan = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/dashboard-indeling", { verborgen: lokaal.verborgen, getoond: lokaal.getoond, compact: lokaal.compact });
      onChange({ verborgen: data.verborgen || [], getoond: data.getoond || [], compact: !!data.compact, standaard: data.standaard ?? null });
      toast.success("Dashboard-indeling opgeslagen"); setOpen(false);
    } catch { toast.error("Opslaan mislukt"); }
    setSaving(false);
  };
  const secties = DASHBOARD_SECTIES.filter((s) => beschikbaar.includes(s.key));
  return (
    <>
      <Button size="sm" variant="outline" className="h-9 gap-1.5 text-xs" onClick={() => setOpen(true)} data-testid="dashboard-indeling-btn">
        <SlidersHorizontal className="h-3.5 w-3.5" /> Indeling
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="dashboard-indeling-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><SlidersHorizontal className="h-5 w-5" /> Dashboard indelen</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Aan = direct zichtbaar, uit = achter "Meer tonen". {indeling.standaard?.length ? "De directie heeft een standaard voor jouw rol ingesteld" : "Standaard zie je de eerste 6 blokken"}; jouw keuze gaat vóór en geldt op al je apparaten.
          </p>
          <label className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 bg-slate-50">
            <span className="text-sm font-medium">Compacte weergave</span>
            <Switch checked={!!lokaal.compact} onCheckedChange={(v) => setLokaal((l) => ({ ...l, compact: v }))} data-testid="dashboard-indeling-compact" />
          </label>
          <div className="divide-y border rounded-md">
            {secties.map((s) => (
              <label key={s.key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span>{s.label}{rolStandaard(s.key) && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">standaard</span>}</span>
                <Switch checked={aan(s.key)} onCheckedChange={() => toggle(s.key)} data-testid={`dashboard-indeling-${s.key}`} />
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLokaal((l) => ({ ...l, verborgen: [], getoond: [] }))} data-testid="dashboard-indeling-reset"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Terug naar standaard</Button>
            <Button onClick={opslaan} disabled={saving} data-testid="dashboard-indeling-opslaan">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Directie (COO): standaard per rol — max 6 blokken; geldt voor iedereen met die rol (eigen keuzes blijven voorgaan). */
export const DashboardStandaardKnop = ({ onGewijzigd }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [rol, setRol] = useState("");
  const [keuze, setKeuze] = useState([]);
  const [saving, setSaving] = useState(false);
  const laad = () => api.get("/dashboard/standaard").then(({ data: d }) => { setData(d); if (!rol && d.rollen?.length) setRol(d.rollen.find((r) => r.key !== "manager_plus")?.key || d.rollen[0].key); }).catch(() => {});
  useEffect(() => { if (open) laad(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data && rol) setKeuze(data.per_rol?.[rol] || []); }, [data, rol]);
  const toggle = (k) => setKeuze((c) => (c.includes(k) ? c.filter((x) => x !== k) : c.length >= MAX_STANDAARD ? (toast.warning(`Maximaal ${MAX_STANDAARD} blokken standaard`), c) : [...c, k]));
  const opslaan = async () => {
    setSaving(true);
    try { const { data: r } = await api.put("/dashboard/standaard", { rol, secties: keuze }); setData((d) => ({ ...d, per_rol: r.per_rol })); toast.success(`Standaard voor ${data.rollen.find((x) => x.key === rol)?.label} opgeslagen (${keuze.length} blokken)`); onGewijzigd?.(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Opslaan mislukt"); }
    setSaving(false);
  };
  const herstel = async () => {
    try { const { data: r } = await api.delete(`/dashboard/standaard/${rol}`); setData((d) => ({ ...d, per_rol: r.per_rol })); toast.success("Standaard hersteld: eerste 6 blokken"); onGewijzigd?.(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Herstellen mislukt"); }
  };
  const ingesteld = !!data?.per_rol?.[rol]?.length;
  return (
    <>
      <Button size="sm" variant="outline" className="h-9 gap-1.5 text-xs" onClick={() => setOpen(true)} data-testid="dashboard-standaard-btn" title="Standaard dashboard per rol (directie)">
        <Building2 className="h-3.5 w-3.5" /> Standaard per rol
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="dashboard-standaard-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" /> Standaard dashboard per rol</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Kies per rol maximaal {MAX_STANDAARD} blokken die iedereen met die rol direct ziet; de rest staat achter "Meer tonen". Medewerkers kunnen daarna zelf nog blokken aan- of uitzetten. Blokken die een rol niet mag zien, blijven sowieso verborgen.</p>
          {!data ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : (
            <>
              <div className="flex items-center gap-2">
                <Select value={rol} onValueChange={setRol}>
                  <SelectTrigger className="h-9 w-64" data-testid="dashboard-standaard-rol"><SelectValue placeholder="Kies rol" /></SelectTrigger>
                  <SelectContent>{data.rollen.filter((r) => r.key !== "manager_plus").map((r) => <SelectItem key={r.key} value={r.key}>{r.label}{data.per_rol?.[r.key]?.length ? ` · ${data.per_rol[r.key].length} ingesteld` : ""}</SelectItem>)}</SelectContent>
                </Select>
                <span className={`text-xs ${ingesteld ? "text-emerald-700" : "text-slate-400"}`} data-testid="dashboard-standaard-status">{ingesteld ? `${keuze.length}/${MAX_STANDAARD} gekozen` : "niet ingesteld → eerste 6 blokken"}</span>
              </div>
              <div className="divide-y border rounded-md">
                {DASHBOARD_SECTIES.map((s) => (
                  <label key={s.key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span>{s.label}</span>
                    <Switch checked={keuze.includes(s.key)} onCheckedChange={() => toggle(s.key)} data-testid={`dashboard-standaard-${s.key}`} />
                  </label>
                ))}
              </div>
              {data.updated_at && <div className="text-[11px] text-slate-400">Laatst gewijzigd {new Date(data.updated_at).toLocaleString("nl-NL")} door {data.updated_by}</div>}
            </>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={herstel} disabled={!ingesteld} data-testid="dashboard-standaard-herstel"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Standaard herstellen</Button>
            <Button onClick={opslaan} disabled={saving || !rol || !data?.bewerkbaar} data-testid="dashboard-standaard-opslaan">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Opslaan voor deze rol</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
